import { useCallback } from 'react';

import { useDispatch } from 'react-redux';

import {
  clearAggregatedField,
  updateAggregatedField,
} from '../../../../store/products/actions';
import { AGGREGATED_FIELD_STATE } from '../../../../store/products/helpers/aggregatedFieldState';
import { useAggregatedBulkTarget } from '../AggregatedBulkTargetContext';
import {
  showAggregatedClearDialog,
  showAggregatedUpdateDialog,
} from '../helpers/aggregatedFieldDialogs';
import { resolveAggregatedDialogLabel } from '../helpers/resolveAggregatedDialogLabel';

export const useBindAggregatedField = (fieldKey, field, fieldLabel, options = {}) => {
  const dispatch = useDispatch();
  const contextTarget = useAggregatedBulkTarget();
  const target = options.target || contextTarget;
  const isMeta = Boolean(options.isMeta);
  const dialogLabel = resolveAggregatedDialogLabel({
    fieldKey,
    fieldLabel,
    dialogLabel: options.dialogLabel,
  });

  const commitUpdate = useCallback((value) => {
    dispatch(updateAggregatedField({
      fieldKey, value, target, isMeta,
    }));
  }, [dispatch, fieldKey, isMeta, target]);

  const commitClear = useCallback(() => {
    dispatch(clearAggregatedField({ fieldKey, target, isMeta }));
  }, [dispatch, fieldKey, isMeta, target]);

  const onChange = useCallback((value, revert) => {
    if (field?.state === AGGREGATED_FIELD_STATE.NOT_APPLICABLE) return;

    showAggregatedUpdateDialog({
      fieldLabel: dialogLabel,
      field,
      onConfirm: () => commitUpdate(value),
      onCancel: revert,
    });
  }, [commitUpdate, dialogLabel, field]);

  const onClear = useCallback((revert) => {
    if (field?.state === AGGREGATED_FIELD_STATE.EMPTY) return;
    if (field?.state === AGGREGATED_FIELD_STATE.NOT_APPLICABLE) return;

    showAggregatedClearDialog({
      fieldLabel: dialogLabel,
      field,
      onConfirm: commitClear,
      onCancel: revert,
    });
  }, [commitClear, dialogLabel, field]);

  return { onChange, onClear };
};

export default useBindAggregatedField;
